/**
 * Operator-facing text for a failed read.
 *
 * The legacy ApiClient throws ApiError, whose `message` is the transport line
 * `API Error: <status> <statusText>`. statusText is empty over HTTP/2, so a
 * page that printed err.message showed "API Error: 500 " to an FI operator.
 * The server's own sentence is in the parsed body, flat ({ message }), nested
 * ({ error: { code, message } }, the ErrorResponse of platform-admin-api), or a
 * legacy string ({ error: 'Member search failed' }).
 *
 * `apiErrorMessage` mirrors apps/platform-admin/src/api/error-message.ts, which
 * reads the same three shapes in the same order.
 */

import { ApiError } from './client';

/** The message an error body carries, or undefined when it carries none. */
export function apiErrorMessage(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const { message, error } = body as { message?: unknown; error?: unknown };
  if (typeof message === 'string' && message.length > 0) return message;
  if (error && typeof error === 'object') {
    const nested = (error as { message?: unknown }).message;
    if (typeof nested === 'string' && nested.length > 0) return nested;
  }
  if (typeof error === 'string' && error.length > 0) return error;
  return undefined;
}

/**
 * The sentence a page shows for a failed read: the server's message when the
 * body carries one, otherwise the page's own fallback. Never the status line.
 */
export function readErrorCopy(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return apiErrorMessage(err.data) ?? fallback;
  }
  return fallback;
}
